/**
 * SF2 patch helpers — normalize / clone / instrument wrappers.
 */
import type { Instrument, Sf2Patch } from "../types";
import {
  DEFAULT_DRUM_SF2_FONT_ID,
  DEFAULT_SF2_FONT_ID,
  DISTORTION_GUITAR_SF2_FONT_ID,
  ELECTRIC_GUITAR_SF2_FONT_ID,
  findSf2Font,
  isDrumSf2Font,
  sf2FontDisplayName,
} from "./catalog";

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function patchForFont(fontId: string, gain: number): Sf2Patch {
  const entry = findSf2Font(fontId);
  return {
    fontId,
    bank: entry?.defaultBank ?? 0,
    program: entry?.defaultProgram ?? 0,
    gain,
  };
}

export function createDefaultSf2Patch(): Sf2Patch {
  return patchForFont(DEFAULT_SF2_FONT_ID, 0.85);
}

/** ElectricGuitar.SF2 (bank 0 / program 0). */
export function createElectricGuitarSf2Patch(): Sf2Patch {
  return patchForFont(ELECTRIC_GUITAR_SF2_FONT_ID, 0.85);
}

/** DistortionGuitar.SF2 (bank 126 / Distorted). */
export function createDistortionGuitarSf2Patch(): Sf2Patch {
  return patchForFont(DISTORTION_GUITAR_SF2_FONT_ID, 0.85);
}

/** Tama RockSTAR (or catalog drum font) on SF2 percussion bank 128. */
export function createDefaultDrumSf2Patch(): Sf2Patch {
  return patchForFont(DEFAULT_DRUM_SF2_FONT_ID, 0.9);
}

/** GM Distortion / Overdriven Guitar (29–30) or matching name. */
export function isDistortionGuitarGm(program: number, nameHint = ""): boolean {
  const programOk = Number.isFinite(program) && (program === 29 || program === 30);
  if (programOk) {
    return true;
  }
  return /overdriven|distortion/i.test(nameHint) && /guitar|\bgt\b/i.test(nameHint);
}

/** GM Guitar family (24–31) or guitar-ish name — excludes distortion when that matches first. */
export function isElectricGuitarGm(program: number, nameHint = ""): boolean {
  if (isDistortionGuitarGm(program, nameHint)) {
    return false;
  }
  const programOk = Number.isFinite(program) && program >= 24 && program <= 31;
  if (programOk) {
    return true;
  }
  return /guitar|nylon|steel\s*gt|\bacoustic\s*gt/i.test(nameHint);
}

/**
 * Melodic SF2 patch for MIDI import: piano / electric guitar / distortion guitar.
 * Patch bank/program follow the SoundFont (not the GM program number).
 */
export function sf2PatchForGmMelodic(program: number, nameHint?: string): Sf2Patch {
  const name = nameHint ?? "";
  if (isDistortionGuitarGm(program, name)) {
    return createDistortionGuitarSf2Patch();
  }
  if (isElectricGuitarGm(program, name)) {
    return createElectricGuitarSf2Patch();
  }
  return createDefaultSf2Patch();
}

/**
 * Preferred GM program when exporting an SF2 track.
 * Guitar fonts keep a stable GM id even though their SF2 program is 0.
 */
export function gmProgramForSf2Patch(patch: Sf2Patch): number {
  if (patch.fontId === DISTORTION_GUITAR_SF2_FONT_ID) {
    return 30; // Distortion Guitar
  }
  if (patch.fontId === ELECTRIC_GUITAR_SF2_FONT_ID) {
    return 27; // Electric Guitar (clean)
  }
  if (patch.bank === 128 || isDrumSf2Font(patch.fontId)) {
    return 0;
  }
  return clamp(Math.round(Number(patch.program) || 0), 0, 127);
}

export function normalizeSf2Patch(raw: Partial<Sf2Patch> | undefined): Sf2Patch {
  const factory = createDefaultSf2Patch();
  if (!raw || typeof raw !== "object") {
    return factory;
  }
  const fontId =
    typeof raw.fontId === "string" && raw.fontId.trim()
      ? raw.fontId.trim().slice(0, 64)
      : factory.fontId;
  const font = findSf2Font(fontId);
  const fallbackBank = font?.defaultBank ?? factory.bank;
  const fallbackProgram = font?.defaultProgram ?? factory.program;
  return {
    fontId,
    bank: clamp(Math.round(Number(raw.bank ?? fallbackBank)), 0, 16383),
    program: clamp(Math.round(Number(raw.program ?? fallbackProgram)), 0, 127),
    gain: clamp(Number(raw.gain ?? factory.gain), 0, 1),
  };
}

export function cloneSf2Patch(patch: Sf2Patch): Sf2Patch {
  return { ...patch };
}

export function sf2Instrument(patch: Sf2Patch): Instrument {
  return { kind: "sf2", patch: cloneSf2Patch(normalizeSf2Patch(patch)) };
}

export function isSf2Instrument(
  instrument: Instrument,
): instrument is { kind: "sf2"; patch: Sf2Patch } {
  return instrument.kind === "sf2";
}

/** Drum kit slot: procedural drums, or SF2 percussion bank / drum font. */
export function isDrumInstrument(instrument: Instrument | undefined): boolean {
  if (!instrument) {
    return false;
  }
  if (instrument.kind === "drums") {
    return true;
  }
  if (instrument.kind === "sf2") {
    return instrument.patch.bank === 128 || isDrumSf2Font(instrument.patch.fontId);
  }
  return false;
}

export function sf2PatchDisplayName(patch: Sf2Patch): string {
  return sf2FontDisplayName(patch.fontId);
}
